import { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { toast } from "react-toastify";

import useGetUserTypes, { useAddUser } from "../add-user-tab/hooks/useAddUserApi";

const labelStyle = {
  fontSize: "14px",
  fontWeight: 600,
  color: "var(--primary-25)",
  marginBottom: "2px",
  display: "block",
};

const inputClass = "form-control height-25 p-0 p-l-5 rounded-0";
const inputStyle = { color: "black" };

const AddUserModalFirmSettings = ({ show, handleClose, refetch }) => {
  const [userName, setUserName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [phoneExt, setPhoneExt] = useState("");
  const [sbn, setSBN] = useState("");
  const [userTypes, setUserTypes] = useState([]);
  const [selectedUserTypes, setSelectedUserTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  const { data } = useGetUserTypes();
  const { saveAddUser } = useAddUser();

  useEffect(() => {
    if (data) setUserTypes(data);
  }, [data]);

  const resetForm = () => {
    setUserName("");
    setFirstName("");
    setLastName("");
    setEmail("");
    setPassword("");
    setShowPassword(false);
    setPhoneExt("");
    setSBN("");
    setSelectedUserTypes([]);
  };

  const handleCheckboxChange = (id) => {
    setSelectedUserTypes((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const r = await saveAddUser({
        username: userName,
        first_name: firstName,
        last_name: lastName,
        email,
        password,
        phone_extension: phoneExt,
        user_types: selectedUserTypes,
        statebarnumber: sbn,
      });
      if (r?.user_active === false && r?.slot_assigned === false) {
        toast.warning(
          r.detail ||
            "No free user slots — the user was created inactive. Purchase more slots to activate them.",
          { autoClose: 8000 }
        );
      }

      resetForm();
      refetch?.();
      handleClose();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      dialogClassName="max-500 modal-dialog-centered"
      contentClassName="custom-modal-new-provider"
      show={show}
      onHide={handleClose}
    >
      <Modal.Header
        style={{ borderBottom: "0px" }}
        className="text-center height-25 p-0 bg-primary popup-heading-color justify-content-center"
      >
        <Modal.Title
          className="mx-auto height-25 font-weight-semibold text-uppercase popup-heading-color d-flex align-items-center"
          style={{ fontSize: "14px", fontWeight: "600" }}
        >
          Add User
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: "5px", minHeight: "350px" }}>
        {/* Username | Email */}
        <div className="d-flex mx-0 align-items-center custom-margin-bottom" style={{ gap: "5px" }}>
          <div className="flex-g-1 p-0">
            <label htmlFor="addUserUsername" style={labelStyle}>
              Username
            </label>
            <input
              id="addUserUsername"
              type="text"
              className={inputClass}
              style={inputStyle}
              placeholder="Enter Username"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
            />
          </div>
          <div className="flex-g-1 p-0">
            <label htmlFor="addUserEmail" style={labelStyle}>
              Email
            </label>
            <input
              id="addUserEmail"
              type="text"
              className={inputClass}
              style={inputStyle}
              placeholder="Enter Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        </div>

        {/* First Name | Last Name */}
        <div className="d-flex mx-0 align-items-center custom-margin-bottom" style={{ gap: "5px" }}>
          <div className="flex-g-1 p-0">
            <label htmlFor="addUserFirstName" style={labelStyle}>
              First Name
            </label>
            <input
              id="addUserFirstName"
              type="text"
              className={inputClass}
              style={inputStyle}
              placeholder="Enter First Name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>
          <div className="flex-g-1 p-0">
            <label htmlFor="addUserLastName" style={labelStyle}>
              Last Name
            </label>
            <input
              id="addUserLastName"
              type="text"
              className={inputClass}
              style={inputStyle}
              placeholder="Enter Last Name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
        </div>

        {/* Password | Phone Extension */}
        <div className="d-flex mx-0 align-items-center custom-margin-bottom" style={{ gap: "5px" }}>
          <div className="flex-g-1 position-relative p-0">
            <label htmlFor="addUserPassword" style={labelStyle}>
              Password
            </label>
            <div className="position-relative">
              <input
                id="addUserPassword"
                type={showPassword ? "text" : "password"}
                className={inputClass}
                style={inputStyle}
                placeholder="Enter Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <span
                className="position-absolute end-0 translate-middle-y me-2 cursor-pointer"
                style={{ top: "10px", right: "10px" }}
                onClick={() => setShowPassword((prev) => !prev)}
              >
                {showPassword ? <FaEyeSlash size={14} /> : <FaEye size={14} />}
              </span>
            </div>
          </div>
          <div className="flex-g-1 p-0">
            <label htmlFor="addUserPhoneExt" style={labelStyle}>
              Phone Extension
            </label>
            <input
              id="addUserPhoneExt"
              type="number"
              className={inputClass}
              style={inputStyle}
              placeholder="Enter Phone Extension"
              value={phoneExt}
              onChange={(e) => setPhoneExt(e.target.value)}
            />
          </div>
        </div>

        {/* SBN */}
        <div className="d-flex mx-0 align-items-center custom-margin-bottom">
          <div className="flex-g-1 p-0">
            <label htmlFor="addUserSbn" style={labelStyle}>
              SBN
            </label>
            <input
              id="addUserSbn"
              type="text"
              className={inputClass}
              style={inputStyle}
              placeholder="Enter SBN"
              value={sbn}
              onChange={(e) => setSBN(e.target.value)}
            />
          </div>
        </div>

        {/* Attach User Types */}
        <div className="d-flex mx-0 custom-margin-bottom">
          <div className="flex-g-1 p-0">
            <label style={labelStyle}>Attach User Types</label>
            <div
              className="d-grid"
              style={{
                gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                gap: "5px",
                display: "grid",
              }}
            >
              {userTypes?.map((userType) => (
                <div
                  key={userType.id}
                  className="m-0 d-flex align-items-center ps-0"
                  style={{
                    gap: "5px",
                    cursor: "pointer",
                  }}
                >
                  <input
                    className="form-check mb-0"
                    type="checkbox"
                    style={{
                      minHeight: "13px",
                    }}
                    id={`ut-${userType.id}`}
                    value={userType.id}
                    onChange={() => handleCheckboxChange(userType.id)}
                    checked={selectedUserTypes.includes(userType.id)}
                  />
                  <label
                    className="form-check-label text-nowrap m-b-0"
                    htmlFor={`ut-${userType.id}`}
                    style={{ color: "black", fontSize: "14px" }}
                  >
                    {userType.name}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer
        className="p-0 mt-0 padding-outside-btn-new-provider"
        style={{ borderTop: "none" }}
      >
        <Button
          className="button-padding-footer-new-provider d-flex align-items-center justify-content-center"
          variant="secondary"
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          variant="success"
          className="button-padding-footer-new-provider d-flex align-items-center justify-content-center"
          onClick={handleSave}
          disabled={loading}
        >
          Add User
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddUserModalFirmSettings;
